/**
 * Shared style tokens for the dark, modern look applied across the public
 * (anonymous-visitor) pages: landing, features & pricing, find a barber,
 * login, and the registration flow. Centralised here so every form/page
 * stays visually consistent without duplicating long sx objects.
 */

export const darkPageBg = '#0f1013';
export const darkAltBg = '#16171c';
export const darkCardBg = '#1b1c22';
export const accent = '#dd8709';
export const accentHover = '#b86e08';

/** sx to spread onto an outlined MUI TextField for dark backgrounds */
export const darkTextFieldSx = {
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: accent },
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.45)' },
    '&.Mui-focused fieldset': { borderColor: accent },
  },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.6)' },
};

/** sx to spread onto a FormControl wrapping a Select for dark backgrounds */
export const darkSelectSx = {
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: accent },
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.25)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.45)' },
    '&.Mui-focused fieldset': { borderColor: accent },
  },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
};

/** Dark dropdown menu styling for a Select's MenuProps */
export const darkSelectMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: darkCardBg,
      color: '#fff',
      '& .MuiMenuItem-root:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
      '& .MuiMenuItem-root.Mui-selected': { bgcolor: 'rgba(221,135,9,0.2)' },
    },
  },
};

/** Dark, elevated card container for auth/registration forms */
export const darkFormContainerSx = {
  bgcolor: darkCardBg,
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 3,
  color: '#fff',
};

/** Primary amber call-to-action button */
export const darkButtonSx = {
  bgcolor: accent,
  color: '#fff',
  fontWeight: 'bold',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    bgcolor: accentHover,
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(221,135,9,0.4)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    bgcolor: 'rgba(255,255,255,0.15)',
    color: 'rgba(255,255,255,0.4)',
  },
};

/** Secondary outlined amber button */
export const darkOutlinedButtonSx = {
  fontWeight: 'bold',
  borderColor: accent,
  color: accent,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    borderColor: accentHover,
    bgcolor: 'rgba(221,135,9,0.15)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(221,135,9,0.25)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
};

/** Full-bleed dark hero banner background using the shared barbershop image */
export const darkHeroSx = (bgImage: string) => ({
  backgroundImage: `linear-gradient(rgba(8,9,12,0.82), rgba(8,9,12,0.9)), url(${bgImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: '#fff',
  textAlign: 'center' as const,
});
